import { useMemo, type ReactNode } from 'react'
import { api as consoleApi } from '../api/client'
import { fanslyMedia } from '../crm/client'
import { LibraryDepsProvider, type LibraryDeps } from '../library/LibraryDeps'
import { createFanslyApi } from './api'
import { FanslyDepsContext, type FanslyDeps } from './deps'

/**
 * Provides the Fansly deps and, for the shared media pieces (useMediaSrc,
 * Avatar, …), the Library deps with Fansly media. The Library's OnlyFans
 * gateway is not available on a Fansly account.
 */
export function FanslyDepsProvider({ value, children }: { value: FanslyDeps; children: ReactNode }) {
  const library = useMemo<LibraryDeps>(
    () => ({
      gateway: () => Promise.reject(new Error('The OnlyFans gateway is not available for a Fansly account')),
      loadMedia: value.loadMedia,
      streamMedia: value.streamMedia,
    }),
    [value],
  )
  return (
    <FanslyDepsContext.Provider value={value}>
      <LibraryDepsProvider value={library}>{children}</LibraryDepsProvider>
    </FanslyDepsContext.Provider>
  )
}

/** The real deps for one account: the console's session and the shared Fansly media queue. */
export function AccountFanslyDeps({ accountId, children }: { accountId: string; children: ReactNode }) {
  const value = useMemo<FanslyDeps>(
    () => ({
      api: createFanslyApi(accountId, { getToken: () => consoleApi.getToken(), refreshToken: () => consoleApi.refreshAccessToken() }),
      loadMedia: (url, signal) => fanslyMedia.load(accountId, url, signal),
      streamMedia: (url) => fanslyMedia.stream(accountId, url),
    }),
    [accountId],
  )
  return <FanslyDepsProvider value={value}>{children}</FanslyDepsProvider>
}
